import { useState, useEffect, useMemo, useRef } from 'react'
import Parse from 'parse'
import { captureException } from '@sentry/react'
import { commenceKlarnaOrder } from '../../components/sessionPayment/components/utils.js'
import { ERRORS } from '../../components/sessionPayment/components/ErrorHandler.jsx'
import { reportAnalyticsCheckoutV2Purchase } from '../../../common/cart/actions.js'
import { getRegion } from '../../../common/region/region.js'
import { useIsAdyenReturn } from '../../components/sessionPayment/hooks/useIsAdyenReturn.js'
import { getCachedPaymentData } from './useCacheItemOrderPaymentData.js'

const SUPPORTED_KLARNA_REGIONS = ['SE', 'DE', 'AT', 'NL', 'DK', 'PL', 'FI', 'FR', 'BE', 'CZ']

const getKlarnaSession = async ({ creditAmount, createKlarnaSession }) => {
  try {
    const session = await createKlarnaSession({
      creditAmount: parseFloat(creditAmount)
    })
    return session
  } catch (error) {
    console.error('Failed to create Klarna session: ', error)
    return null
  }
}

const loadKlarnaWidgets = ({ paymentOptions, setKlarnaPaymentOptions }) => {
  paymentOptions.forEach((paymentOption) => {
    // eslint-disable-next-line no-undef
    Klarna.Payments.load(
      {
        container: `#klarna-payments-container-${paymentOption.identifier}`,
        payment_method_category: paymentOption.identifier
      },
      {},
      (res) => {
        if (!res.show_form) {
          setKlarnaPaymentOptions((prevOptions) =>
            prevOptions.filter((option) => option.identifier !== paymentOption.identifier)
          )
        }
      }
    )
  })
}

const useKlarnaPaymentMethods = () => {
  const klarnaPaymentsPaynowRef = useRef(null)
  const klarnaPaymentsPaylaterRef = useRef(null)
  const klarnaPaymentsPayovertimeRef = useRef(null)

  const klarnaPaymentMethodRefs = useMemo(
    () => ({
      pay_now: klarnaPaymentsPaynowRef,
      pay_later: klarnaPaymentsPaylaterRef,
      pay_over_time: klarnaPaymentsPayovertimeRef
    }),
    [klarnaPaymentsPaynowRef, klarnaPaymentsPaylaterRef, klarnaPaymentsPayovertimeRef]
  )
  return { klarnaPaymentMethodRefs }
}

const useKlarnaCheckout = ({
  creditAmount,
  nothingToPayWithMoney,
  shouldUseKlarna,
  createKlarnaOrder,
  createKlarnaSession
}) => {
  const [klarnaPaymentOptions, setKlarnaPaymentOptions] = useState([])
  const [klarnaSessionId, setKlarnaSessionId] = useState()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()
  const region = getRegion()
  const isNotSupportedRegion = !SUPPORTED_KLARNA_REGIONS.includes(region)
  const isAdyenReturn = useIsAdyenReturn()

  useEffect(() => {
    if (!shouldUseKlarna || nothingToPayWithMoney) return
    if (isNotSupportedRegion) {
      console.warn(`Klarna not supported in ${region}`)
      return
    }

    const initializeKlarna = async () => {
      try {
        const session = await getKlarnaSession({ creditAmount, createKlarnaSession })

        if (!session || session.error_code || session.error_message) {
          console.error('Error creating the session:', session)
          return
        }

        setKlarnaSessionId(session.session_id)
        setKlarnaPaymentOptions(session.payment_method_categories)

        window.klarnaAsyncCallback = () => {
          // eslint-disable-next-line no-undef
          Klarna.Payments.init({
            client_token: session.client_token
          })
          loadKlarnaWidgets({
            paymentOptions: session.payment_method_categories,
            setKlarnaPaymentOptions
          })
        }

        const script = document.createElement('script')
        script.src = 'https://x.klarnacdn.net/kp/lib/v1/api.js'
        script.async = true
        document.body.appendChild(script)
      } catch (error) {
        console.error('Error initializing Klarna:', error)
        setKlarnaPaymentOptions([])
      }
    }

    if (!isAdyenReturn) initializeKlarna()

    return () => {
      const existingScript = document.querySelector(
        'script[src="https://x.klarnacdn.net/kp/lib/v1/api.js"]'
      )
      if (existingScript && document.body.contains(existingScript)) {
        document.body.removeChild(existingScript)
      }
      if (window.klarnaAsyncCallback) {
        delete window.klarnaAsyncCallback
      }
    }
  }, [
    creditAmount,
    shouldUseKlarna,
    nothingToPayWithMoney,
    createKlarnaSession,
    region,
    isNotSupportedRegion,
    isAdyenReturn
  ])

  const createOrder = async (data) => {
    setLoading(true)
    const paymentData = getCachedPaymentData()
    const paymentOption = data.paymentOptions
    const { firstName, lastName, phone, country, zipCode, street, city } = paymentData.address
    const address = {
      city,
      country,
      email: paymentData.email,
      family_name: lastName,
      given_name: firstName,
      phone,
      postal_code: zipCode,
      street_address: street
    }

    try {
      await commenceKlarnaOrder({
        consent: data.dataUsageConsent,
        email: paymentData.email,
        sessionId: klarnaSessionId
      })
      // eslint-disable-next-line no-undef
      Klarna.Payments.authorize(
        {
          payment_method_category: paymentOption
        },
        {
          billing_address: address
        },
        async (res) => {
          if (!res.approved) {
            setError({ type: ERRORS.KLARNA_ERROR, details: res })
            setLoading(false)
            return
          }
          if (res.approved && res.authorization_token) {
            setLoading(true)
            await createKlarnaOrder({
              authorizationToken: res.authorization_token,
              dataUsageConsent: data.dataUsageConsent,
              klarnaPaymentMethod: paymentOption,
              sessionId: klarnaSessionId
            })
              .then(async (result) => {
                await reportAnalyticsCheckoutV2Purchase(result.paymentId)
                window.location.assign(result.redirect_url)
              })
              .catch((error) => {
                captureException(error)
                if (error instanceof Parse.Error) {
                  setError({ type: ERRORS.PARSE_ERROR, details: error })
                } else {
                  setError({ type: ERRORS.KLARNA_ERROR, details: error })
                }
              })
              .finally(() => {
                setLoading(false)
              })
          }
        }
      )
    } catch (error) {
      captureException(error)
      if (error instanceof Parse.Error) {
        setError({ type: ERRORS.PARSE_ERROR, details: error })
      }
    } finally {
      setLoading(false)
    }
  }

  return {
    klarnaPaymentOptions,
    loading,
    error,
    createOrder
  }
}

export { useKlarnaPaymentMethods, useKlarnaCheckout }
